import { useTranslation } from 'react-i18next';

import { Sun, Waves } from 'lucide-react';

import { ItemRow } from './ResultRow';
import { Card, Pill, SectionHeadCard } from '../../../../components';
import {
  POSITION_STEPS,
  brightnessPosition,
  resonanceStylePoint,
  spreadPosition,
  stepOfPosition,
} from '../../resultCopy';

import type { PillTone } from '../../../../components';
import type { DiagnosisRecord } from '../../record';
import type { PositionStep } from '../../resultCopy';

/**
 * 공명 구간 — 울림이 **밝은 쪽인지 어두운 쪽인지**, **넓은지 모였는지**(시안 §8.9
 * `1001:9514`): `핵심 포인트` 두 행(아이콘·제목·축·배지 + 슬라이더) · `공명 스타일` 지도(2×2 사분면 위에
 * 다섯 칸 격자).
 *
 * 두 축 다 눈금이 미보정이라(`BRIGHTNESS_SCALE` 주석: 데모 4테이크 실측) 배지는
 * `밝은 편`까지만 말하고 점수를 안 낸다. 자·뒤집기는 전부 `resultCopy`의 순수 함수가
 * 갖는다 — 슬라이더와 산점도가 같은 함수를 통과해야 두 그림이 반대를 가리키지 않는다.
 */

/** 배지 색 — 시안은 `밝은 편`을 노랑, `균형적`을 민트로 그린다. 다섯 칸은 양끝 둘씩 같은 색. */
const TONE: Record<PositionStep, PillTone> = {
  veryHigh: 'orange',
  high: 'orange',
  mid: 'mint',
  low: 'gray',
  veryLow: 'gray',
};

/** 칸 경계의 위치(0..1) — 다섯 칸이면 넷. 슬라이더와 지도가 같은 경계를 쓴다. */
const STEP_EDGES = POSITION_STEPS.slice(1).map(
  (_, i) => (i + 1) / POSITION_STEPS.length,
);

/** 산점도 안쪽 여백(뷰박스 100 기준). 점이 테두리에 걸치면 값이 잘린 것처럼 보인다. */
const PAD = 8;
const SPAN = 100 - PAD * 2;

const Slider = ({
  position,
  left,
  right,
}: {
  position: number;
  left: string;
  right: string;
}) => (
  <div
    className="flex items-center gap-3 px-[22px] text-xs text-bk-600"
    aria-hidden="true"
  >
    <span className="shrink-0">{left}</span>
    <div className="relative h-1.5 flex-1 rounded-full bg-bk-200">
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-primary"
        style={{ width: `${position * 100}%` }}
      />
      {/* 칸 경계 — 배지의 다섯 칸이 슬라이더 어디서 갈리는지(TestFlight #59). */}
      {STEP_EDGES.map((edge) => (
        <span
          key={edge}
          data-testid="slider-step"
          className="absolute inset-y-0 w-px -translate-x-1/2 bg-bk-400"
          style={{ left: `${edge * 100}%` }}
        />
      ))}
      <span
        className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary ring-[3px] ring-white shadow-chip"
        style={{ left: `${position * 100}%` }}
      />
    </div>
    <span className="shrink-0">{right}</span>
  </div>
);

/**
 * 공명 스타일 지도 — 시안의 2×2 사분면(가운데 십자) 위에 두 축의 다섯 칸 격자를 옅게 깐다.
 * **점은 두 축이 다 있을 때만 찍힌다** — 빈 지도는 축이 무엇인지
 * 알려 주는 그림이지만, 원점에 찍힌 점은 잰 값과 구별되지 않는 거짓말이다.
 */
const StyleMap = ({ point }: { point: { x: number; y: number } | null }) => {
  const { t } = useTranslation();
  const cx = PAD + (point?.x ?? 0) * SPAN;
  // 뷰박스의 y는 아래로 자란다 — 밝을수록(y=1) 위로 가도록 뒤집어 그린다.
  const cy = PAD + (1 - (point?.y ?? 0)) * SPAN;

  return (
    <div className="flex flex-col items-center gap-2 px-[18px]">
      <p className="text-xs font-medium text-bk-800">
        {t('web.result.resonance.axis.top')}
      </p>
      <div className="flex w-full items-center gap-2">
        <span className="w-10 shrink-0 text-right text-[11px] leading-tight text-bk-600">
          {t('web.result.resonance.axis.left')}
        </span>
        <div className="relative flex-1">
          <svg viewBox="0 0 100 100" className="w-full" role="presentation">
            <rect
              x={PAD}
              y={PAD}
              width={SPAN}
              height={SPAN}
              rx="2"
              fill="hsl(var(--primary) / 0.06)"
            />
            {/* 다섯 칸 격자 — 두 축의 칸 경계(TestFlight #59: 매핑 구간 세분). 사분면 십자보다
                옅게 깔아 시안의 2×2 읽기는 그대로 둔다. */}
            {STEP_EDGES.map((edge) => (
              <g key={edge}>
                <line
                  data-testid="map-grid"
                  x1={PAD + edge * SPAN}
                  y1={PAD}
                  x2={PAD + edge * SPAN}
                  y2={100 - PAD}
                  stroke="hsl(var(--primary) / 0.12)"
                  strokeWidth="0.3"
                />
                <line
                  data-testid="map-grid"
                  x1={PAD}
                  y1={PAD + edge * SPAN}
                  x2={100 - PAD}
                  y2={PAD + edge * SPAN}
                  stroke="hsl(var(--primary) / 0.12)"
                  strokeWidth="0.3"
                />
              </g>
            ))}
            <line
              x1="50"
              y1={PAD}
              x2="50"
              y2={100 - PAD}
              stroke="hsl(var(--primary) / 0.25)"
              strokeWidth="0.6"
            />
            <line
              x1={PAD}
              y1="50"
              x2={100 - PAD}
              y2="50"
              stroke="hsl(var(--primary) / 0.25)"
              strokeWidth="0.6"
            />
            {point && (
              <circle
                cx={cx}
                cy={cy}
                r="3.5"
                fill="hsl(var(--primary))"
                role="img"
                aria-label={t('web.result.resonance.here')}
              />
            )}
          </svg>
          {point && (
            <span
              aria-hidden="true"
              className="absolute -translate-x-1/2 -translate-y-[calc(100%+10px)] whitespace-nowrap rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white"
              style={{ left: `${cx}%`, top: `${cy}%` }}
            >
              {t('web.result.resonance.here')}
            </span>
          )}
        </div>
        <span className="w-10 shrink-0 text-[11px] leading-tight text-bk-600">
          {t('web.result.resonance.axis.right')}
        </span>
      </div>
      <p className="text-xs font-medium text-bk-800">
        {t('web.result.resonance.axis.bottom')}
      </p>
    </div>
  );
};

export const ResonanceStyleSection = ({
  record,
}: {
  record: DiagnosisRecord;
}) => {
  const { t } = useTranslation();
  const brightness = record.brightnessHz;
  const bandwidth = record.bandwidthHz;

  /**
   * 두 축은 **같은 줄 모양**이다 — 이름·값·미터 위치만 다르다. 표로 두면 줄에 손댈 일이
   * (미측정 처리든 배지든) 한 번에 두 축에 같이 간다.
   */
  const axes = [
    {
      key: 'brightness',
      value: brightness,
      position: brightnessPosition,
      icon: <Sun className="h-5 w-5 text-primary" aria-hidden="true" />,
    },
    {
      key: 'spread',
      value: bandwidth,
      position: spreadPosition,
      icon: <Waves className="h-5 w-5 text-primary" aria-hidden="true" />,
    },
  ] as const;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 py-4">
        <SectionHeadCard
          size="md"
          className="mx-[18px]"
          title={t('web.result.tabs.keyPoints')}
        />
        <div className="flex flex-col gap-6">
          {axes.map(({ key, value, position, icon }) => {
            const level =
              value == null ? null : stepOfPosition(position(value));
            const [left, right] = t(`web.result.resonance.${key}Axis`).split(
              ' ↔ ',
            );
            return (
              <div key={key} className="flex flex-col gap-3">
                <ItemRow
                  icon={icon}
                  title={t(`web.result.resonance.${key}`)}
                  desc={t(`web.result.resonance.${key}Axis`)}
                  right={
                    <Pill tone={level ? TONE[level] : 'gray'}>
                      {level
                        ? t(`web.result.resonance.${key}Level.${level}`)
                        : t('web.result.pending')}
                    </Pill>
                  }
                />
                {value != null && (
                  <Slider
                    position={position(value)}
                    left={left ?? ''}
                    right={right ?? ''}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-8 py-4">
        <Card className="mx-[18px] px-5 py-[18px]">
          <h3 className="text-lg font-semibold tracking-[-0.09px] text-bk-900">
            {t('web.result.resonance.style')}
          </h3>
          <p className="mt-2 text-sm leading-[1.35] text-bk-800">
            {t('web.result.resonance.styleNote')}
          </p>
        </Card>
        <StyleMap point={resonanceStylePoint(record)} />
      </div>
    </div>
  );
};
